import { appEnv } from './env'

/**
 * Cliente do `spa-bridge` do portal-sso-flexibase (padrão `web-spa-bridge-v1`).
 *
 * O bridge é um sidecar que fala OIDC com o SSO e guarda a sessão num cookie
 * HttpOnly (`portal_sso_bridge_session`). O browser nunca vê access token nem
 * refresh token: só o que `GET /api/session` devolve.
 *
 * Tudo aqui é same-origin. O nginx do app (produção) e o proxy do Vite (dev)
 * expõem `/auth/*` e `/api/session` sob o mesmo host — sem isso o cookie não
 * viaja e o login entra em loop.
 */

export interface BridgeSession {
  readonly userId: string
  readonly email: string
  readonly displayName: string
  /** JWT que autoriza PostgREST/RLS. Emitido pelo SSO, repassado pelo bridge. */
  readonly supabaseAccessToken: string | null
}

/**
 * O token vive em memória, nunca em localStorage.
 *
 * Guardá-lo em storage o deixaria legível por qualquer script da página e
 * sobrevivendo ao logout — o cookie HttpOnly do bridge existe justamente para
 * evitar isso. Recarregar a página busca de novo em `/api/session`.
 */
let supabaseAccessToken: string | null = null

/**
 * Guarda de logout: entre o clique em "sair" e a navegação, o app pode
 * renderizar de novo e ver `anonimo`. Sem esta flag, o guard dispararia um
 * login novo e o usuário voltaria logado — o clássico "não consigo sair".
 */
let signingOut = false

export function getBridgeSupabaseAccessToken(): string | null {
  return supabaseAccessToken
}

export function clearBridgeSupabaseAccessToken(): void {
  supabaseAccessToken = null
}

export function getIsSigningOut(): boolean {
  return signingOut
}

/** Um JWT tem três segmentos separados por ponto. Qualquer outra coisa é lixo. */
function isJwtLike(value: unknown): value is string {
  return typeof value === 'string' && value.split('.').length === 3
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return null
}

/**
 * O formato exato do payload não é garantido pelo contrato do bridge, e variou
 * entre gerações: o PMC lê os campos na raiz, o SGI já precisou olhar em
 * `metadata`. Aceitar as formas conhecidas é mais barato que descobrir a
 * divergência em produção.
 */
function extractSupabaseAccessToken(payload: Record<string, unknown>): string | null {
  const session = (payload.session ?? payload) as Record<string, unknown>
  const metadata = (session.metadata ?? {}) as Record<string, unknown>

  const candidate = firstString(
    session.supabaseAccessToken,
    session.supabase_access_token,
    metadata.supabaseAccessToken,
    metadata.supabase_access_token,
  )

  return isJwtLike(candidate) ? candidate : null
}

function extractUser(payload: Record<string, unknown>): {
  userId: string | null
  email: string
  displayName: string
} {
  const session = (payload.session ?? payload) as Record<string, unknown>
  const user = (session.user ?? session) as Record<string, unknown>

  return {
    userId: firstString(user.userId, user.user_id, user.sub, user.id),
    email: firstString(user.email) ?? '',
    displayName: firstString(user.name, user.displayName, user.full_name, user.email) ?? '',
  }
}

/**
 * Lê a sessão corrente. Devolve `null` para anônimo.
 *
 * Qualquer resposta que não seja JSON de sessão conta como anônimo. Isso não é
 * paranoia: quando a borda está desalinhada, `/api/session` cai no fallback da
 * SPA e devolve o `index.html`. Interpretar isso como "autenticado" produziria
 * um app logado sem token, falhando em toda query com 401.
 */
export async function fetchBridgeSession(): Promise<BridgeSession | null> {
  let response: Response
  try {
    response = await fetch(`${appEnv.bridgeBaseUrl}/api/session`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
  } catch {
    return null
  }

  if (!response.ok) return null
  if (!response.headers.get('content-type')?.includes('application/json')) return null

  let payload: Record<string, unknown>
  try {
    payload = (await response.json()) as Record<string, unknown>
  } catch {
    return null
  }

  if (payload.authenticated === false) return null

  const { userId, email, displayName } = extractUser(payload)
  if (!userId) return null

  supabaseAccessToken = extractSupabaseAccessToken(payload)

  return { userId, email, displayName, supabaseAccessToken }
}

export function buildBridgeLoginUrl(returnTo: string = window.location.href): string {
  const params = new URLSearchParams({ appId: appEnv.appId, returnTo })
  return `${appEnv.bridgeBaseUrl}/auth/login?${params.toString()}`
}

export function redirectToBridgeLogin(returnTo?: string): void {
  window.location.assign(buildBridgeLoginUrl(returnTo))
}

/**
 * Encerra a sessão no bridge e devolve a URL de logout do SSO, para que a
 * sessão do portal também caia. Sair só do app deixaria o próximo login
 * passando direto, sem pedir credencial.
 */
export async function signOutFromBridge(): Promise<string | null> {
  signingOut = true
  clearBridgeSupabaseAccessToken()

  try {
    const response = await fetch(`${appEnv.bridgeBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return null
    const payload = (await response.json()) as { logoutUrl?: string }
    return firstString(payload.logoutUrl)
  } catch {
    return null
  }
}
